import { Common } from "@common/types";
import { default as classNames } from "classnames";
import * as React from "react";

import "./style.less";

type ID = string | number;

export type MasonryGridItem = Common.WithId<ID> & {};

export interface MasonryGridInnerProps<TItemProps extends {} = {}, TItem extends MasonryGridItem = MasonryGridItem> {
  className?: string;
  columns: number;
  containerClassName?: string;
  dataStep?: string;
  gutter?: number | string;
  noItemsMessage?: string;
  itemClassName?: string;
  itemContainerStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  onItemClick?: (id: ID) => void;
  onItemDoubleClick?: (id: ID) => void;
  selectedItemId?: ID | null;

  itemProps?: Partial<TItemProps>;
  items: Array<TItem | ID>;
  ItemComponent: React.ComponentType<TItemProps & TItem & { isSelected?: boolean }>;
}

export type MasonryGridProps<
  TItemProps extends {} = {},
  TItem extends MasonryGridItem = MasonryGridItem
> = MasonryGridInnerProps<TItemProps, TItem>;

const MasonryGrid = <TItemProps extends {} = {}, TItem extends MasonryGridItem = MasonryGridItem>(
  props: MasonryGridProps<TItemProps, TItem>
) => {
  const items = React.useMemo(
    (): TItem[] => props.items.map<TItem>((x) => (typeof x === "string" ? ({ id: x } as any) : x)),
    [props.items]
  );

  const columnItems = React.useMemo((): Array<TItem[]> => {
    return items.reduce((res, x, i) => {
      const index = i % props.columns;
      res[index] = [...res[index], x];
      return res;
    }, Array.from({ length: props.columns }, () => []) as Array<TItem[]>);
  }, [items, props.columns]);

  const render = () => {
    if (!!props.noItemsMessage && !props.items.length)
      return <div className="no_items_message">{props.noItemsMessage}</div>;

    return (
      <>
        {Array.from({ length: props.columns }).map((_, i) => (
          <div key={i} className="grid_column" style={{ gap: props.gutter }}>
            {columnItems[i].map((x) => (
              <div
                key={x.id}
                className={classNames("grid_item", props.itemClassName, {
                  selected: props.selectedItemId === x.id
                })}
                data-step={props.dataStep}
                onClick={props.onItemClick ? () => props.onItemClick!(x.id) : undefined}
                onDoubleClick={props.onItemDoubleClick ? () => props.onItemDoubleClick!(x.id) : undefined}
                style={props.itemContainerStyle}
              >
                <props.ItemComponent {...((props.itemProps || {}) as any)} {...x} />
              </div>
            ))}
          </div>
        ))}
      </>
    );
  };

  const style = React.useMemo(() => ({ ...props.style, gap: props.gutter }), [props.gutter, props.style]);

  return (
    <div className={classNames("masonry_grid", props.containerClassName)} style={style}>
      {render()}
    </div>
  );
};

export default MasonryGrid;
